"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import NextLink from "next/link";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  CONSENT_BANNER_OPEN_EVENT,
  detectUserRegion,
  getMeasurementId,
  hasGlobalPrivacyControl,
  readStoredConsent,
  updateConsentGranular,
  type StoredConsentChoice,
} from "../../lib/analytics";
import { relayRedditAdConsent } from "../../lib/reddit/consent-relay";
import { relayAnalyticsConsent } from "../../lib/analytics/consent-relay";
import { useAuth } from "../../util/auth";

type BannerState =
  | { phase: "loading" }
  | { phase: "hidden" }
  | { phase: "visible"; reason: "required" | "user_requested" };

interface DualChoice {
  ad_storage: StoredConsentChoice;
  analytics_storage: StoredConsentChoice;
}

/**
 * Region-gated cookie consent banner with independent toggles for the
 * two Consent Mode v2 channels we use:
 *
 *   - Analytics (analytics_storage) — GA4 behavior measurement.
 *   - Advertising (ad_storage) — Reddit CAPI + Google Ads conversion
 *     tracking; also drives ad_user_data / ad_personalization.
 *
 * Quick actions ("Accept all", "Decline all") flip both toggles at once.
 * "Customize" reveals the granular switches so a user can accept one and
 * decline the other — required for GDPR/PECR purpose-specific consent.
 *
 * Behavior:
 * - On mount, if the user has already made a choice (cookie present): hidden.
 * - Otherwise we detect the user's country via Cloudflare trace.
 *   - If the country is in a consent-required region (EU/EEA/UK/CH/BR): show
 *     the banner.
 *   - If it is NOT in a consent-required region: silently grant both
 *     channels, persist the choice, and stay hidden. Non-EU users can
 *     still opt out later via the "Cookie preferences" link in the
 *     footer.
 *   - If detection fails (timeout, network error): fail-safe to showing the
 *     banner, matching the Consent Mode v2 default-denied posture.
 *   - If GPC is active: silently deny ad_storage (legal requirement) and
 *     also deny analytics_storage (conservative default — user can
 *     customize from the footer).
 * - Listens for the CONSENT_BANNER_OPEN_EVENT so the footer link (or any
 *   other UI surface) can re-open the banner to let the user change their
 *   mind.
 */
export default function ConsentBanner() {
  const measurementId = getMeasurementId();
  // ``useAuth()`` is untyped JS (see src/util/auth.js); narrow to the only
  // field this component consumes. Aligned with existing admin screens
  // (PartnerProgramAdminSection, EmbodimentExplorer) that cast the same way.
  const auth = useAuth() as { user?: { accessToken?: string } | false | null };
  // Ref mirror of the current access token so our mount-time effect (region
  // detect, GPC check, silent grant) can relay consent without listing the
  // auth user in its dependency array — which would otherwise re-run region
  // detection every time Firebase refreshes the ID token.
  const accessTokenRef = useRef<string | null>(null);
  accessTokenRef.current =
    auth.user && typeof auth.user === "object" && auth.user.accessToken
      ? auth.user.accessToken
      : null;

  const [state, setState] = useState<BannerState>({ phase: "loading" });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Granular toggle state for the Customize panel. Default is "granted"
  // for both so "Customize -> Save" without touching anything is
  // equivalent to "Accept all" — least-surprising for users who opened
  // the panel to inspect rather than change settings. Region-required
  // denials come from the quick Decline button instead.
  const [pendingAdStorage, setPendingAdStorage] = useState<StoredConsentChoice>(
    "granted"
  );
  const [pendingAnalytics, setPendingAnalytics] = useState<StoredConsentChoice>(
    "granted"
  );

  const applyChoice = useCallback((choice: DualChoice) => {
    updateConsentGranular(choice);
    void relayRedditAdConsent(choice.ad_storage, accessTokenRef.current);
    void relayAnalyticsConsent(
      choice.analytics_storage,
      accessTokenRef.current
    );
    setState({ phase: "hidden" });
    setCustomizeOpen(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    applyChoice({ ad_storage: "granted", analytics_storage: "granted" });
  }, [applyChoice]);

  const handleDeclineAll = useCallback(() => {
    applyChoice({ ad_storage: "denied", analytics_storage: "denied" });
  }, [applyChoice]);

  const handleSaveCustom = useCallback(() => {
    applyChoice({
      ad_storage: pendingAdStorage,
      analytics_storage: pendingAnalytics,
    });
  }, [applyChoice, pendingAdStorage, pendingAnalytics]);

  useEffect(() => {
    if (!measurementId) return;
    if (typeof window === "undefined") return;

    let cancelled = false;
    const stored = readStoredConsent();
    if (stored) {
      // Seed the Customize toggles with the user's existing choice so a
      // re-open via the footer link presents their current settings.
      setPendingAdStorage(stored.ad_storage);
      setPendingAnalytics(stored.analytics_storage);
      setState({ phase: "hidden" });
    } else if (hasGlobalPrivacyControl()) {
      // Browser is signalling GPC. CCPA/CPRA + Colorado CPA require honoring
      // this as an opt-out of ad_storage without further user interaction.
      // We also deny analytics_storage conservatively: the user can grant
      // it later via the footer "Cookie preferences" link if desired.
      updateConsentGranular({
        ad_storage: "denied",
        analytics_storage: "denied",
      });
      void relayRedditAdConsent("denied", accessTokenRef.current);
      void relayAnalyticsConsent("denied", accessTokenRef.current);
      setPendingAdStorage("denied");
      setPendingAnalytics("denied");
      setState({ phase: "hidden" });
    } else {
      void (async () => {
        const region = await detectUserRegion();
        if (cancelled) return;
        if (region.consentRequired) {
          setState({ phase: "visible", reason: "required" });
        } else {
          // Non-EU user with no stored choice and no GPC signal: silently
          // grant both channels and persist. Users retain opt-out via
          // the footer link.
          updateConsentGranular({
            ad_storage: "granted",
            analytics_storage: "granted",
          });
          void relayRedditAdConsent("granted", accessTokenRef.current);
          void relayAnalyticsConsent("granted", accessTokenRef.current);
          setState({ phase: "hidden" });
        }
      })();
    }

    const onOpen = () => {
      setState({ phase: "visible", reason: "user_requested" });
    };
    window.addEventListener(CONSENT_BANNER_OPEN_EVENT, onOpen);

    return () => {
      cancelled = true;
      window.removeEventListener(CONSENT_BANNER_OPEN_EVENT, onOpen);
    };
  }, [measurementId]);

  if (!measurementId) return null;
  if (state.phase !== "visible") return null;

  return (
    <Box
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      sx={{
        position: "fixed",
        left: { xs: 8, sm: 16 },
        right: { xs: 8, sm: 16 },
        bottom: { xs: 8, sm: 16 },
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          pointerEvents: "auto",
          maxWidth: 720,
          width: "100%",
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              We use cookies for analytics and advertising
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We use optional cookies to measure how people use Neurorobotics
              Studio and to attribute downloads and purchases to the
              campaigns that brought you here. You can accept all, decline
              all, or customize each purpose — your choice is remembered.
              See our{" "}
              <Link
                component={NextLink}
                href="/legal/privacy-policy"
                underline="always"
                color="primary"
              >
                Privacy Policy
              </Link>
              {" "}for details.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent={{ xs: "flex-end", md: "flex-end" }}
            sx={{ flexShrink: 0 }}
          >
            <Button
              onClick={() => setCustomizeOpen((v) => !v)}
              variant="text"
              color="inherit"
              size="small"
              aria-expanded={customizeOpen}
            >
              Customize
            </Button>
            <Button
              onClick={handleDeclineAll}
              variant="text"
              color="inherit"
              size="small"
            >
              Decline all
            </Button>
            <Button
              onClick={handleAcceptAll}
              variant="contained"
              color="primary"
              size="small"
            >
              Accept all
            </Button>
          </Stack>
        </Stack>

        <Collapse in={customizeOpen} unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={pendingAnalytics === "granted"}
                  onChange={(_e, checked) =>
                    setPendingAnalytics(checked ? "granted" : "denied")
                  }
                  inputProps={{
                    "aria-label": "Analytics cookies",
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Analytics
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Help us measure how people use the product so we can
                    improve it (Google Analytics 4).
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={pendingAdStorage === "granted"}
                  onChange={(_e, checked) =>
                    setPendingAdStorage(checked ? "granted" : "denied")
                  }
                  inputProps={{
                    "aria-label": "Advertising cookies",
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Advertising
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Attribute downloads and purchases to the ads that
                    brought you here (Google Ads, Reddit). Turning this
                    off does not change what ads you see elsewhere.
                  </Typography>
                </Box>
              }
            />
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{ pt: 0.5 }}
            >
              <Button
                onClick={handleSaveCustom}
                variant="outlined"
                color="primary"
                size="small"
              >
                Save preferences
              </Button>
            </Stack>
          </Stack>
        </Collapse>
      </Paper>
    </Box>
  );
}
